import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { GridColumns, GridRowId, GridSelectionModel } from '@mui/x-data-grid-pro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCustomer } from 'src/api/customer'
import {
  invalidateIdentityUserSyncPreview,
  useApplyIdentitySync,
  useIdentitySyncPreview,
} from 'src/api/identity-user-sync'
import { invalidateSystemUserCache } from 'src/api/user'
import GenericTable from 'src/components/table/GenericTable'
import {
  IIdentityUserSyncEmailChange,
  IIdentityUserSyncLinkIdentity,
  IdentitySyncRow,
} from 'src/types/identity-user-sync'

function rowKey(row: IdentitySyncRow) {
  if (row.kind === 'email') {
    const d = row.data
    return `email:${d.localUserId}:${d.identityServerUserId}`
  }
  const d = row.data
  return `link:${d.localUserId}:${d.identityServerUserId}`
}

interface IdentityUserSyncDialogProps {
  pop: (data?: unknown) => void
}

type IdentitySyncGridRow = {
  id: string
  kind: IdentitySyncRow['kind']
  localUserId: string
  identityServerUserId: string
  currentEmail: string
  newEmail: string
}

const IdentityUserSyncDialog = ({ pop }: IdentityUserSyncDialogProps) => {
  const { t } = useTranslation(['users', 'common', 'common-api-message'])
  const { data, isLoading, isFetching, refetch } = useIdentitySyncPreview(true)
  const { mutateAsync: applySync, isLoading: isApplying } = useApplyIdentitySync()
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([])

  const rows: IdentitySyncRow[] = useMemo(() => {
    if (!data) return []
    const emailRows = data.emailChanges.map(
      (r): IdentitySyncRow => ({ kind: 'email', data: r }),
    )
    const linkRows = data.linkIdentityChanges.map(
      (r): IdentitySyncRow => ({ kind: 'link', data: r }),
    )
    return [...emailRows, ...linkRows]
  }, [data])

  const gridRows: IdentitySyncGridRow[] = useMemo(
    () =>
      rows.map(r => ({
        id: rowKey(r),
        kind: r.kind,
        localUserId: r.data.localUserId,
        identityServerUserId: r.data.identityServerUserId,
        currentEmail: r.kind === 'email' ? r.data.currentEmail : r.data.email,
        newEmail: r.kind === 'email' ? r.data.newEmail : '—',
      })),
    [rows],
  )

  useEffect(() => {
    if (!gridRows.length) {
      setSelectionModel([])
      return
    }
    setSelectionModel(gridRows.map(r => r.id))
  }, [gridRows])

  const selectedPayload = useMemo(() => {
    const emailChanges: IIdentityUserSyncEmailChange[] = []
    const linkIdentityChanges: IIdentityUserSyncLinkIdentity[] = []
    const selectedIds = new Set(selectionModel.map(String))
    rows.forEach(r => {
      if (!selectedIds.has(rowKey(r))) return
      if (r.kind === 'email') emailChanges.push(r.data)
      else linkIdentityChanges.push(r.data)
    })
    return { emailChanges, linkIdentityChanges }
  }, [rows, selectionModel])

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'changeKind',
        headerName: t('identitySyncChangeKind', { ns: 'users' }),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) =>
          row.kind === 'email'
            ? t('identitySyncChangeKindUpdateEmail', { ns: 'users' })
            : t('identitySyncChangeKindSetIdentityServerId', { ns: 'users' }),
      },
      {
        field: 'localUserId',
        headerName: t('identitySyncLocalUserId', { ns: 'users' }),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ value }) => (
          <Box sx={{ wordBreak: 'break-all', whiteSpace: 'normal', py: 0.5 }}>{value}</Box>
        ),
      },
      {
        field: 'identityServerUserId',
        headerName: t('identitySyncIsUserId', { ns: 'users' }),
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ value }) => (
          <Box sx={{ wordBreak: 'break-all', whiteSpace: 'normal', py: 0.5 }}>{value}</Box>
        ),
      },
      {
        field: 'currentEmail',
        headerName: t('identitySyncCurrentEmail', { ns: 'users' }),
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
      {
        field: 'newEmail',
        headerName: t('identitySyncNewEmail', { ns: 'users' }),
        flex: 1,
        minWidth: 140,
        sortable: false,
      },
    ],
    [t],
  )

  const handleSelectionChange = useCallback((model: GridSelectionModel) => {
    setSelectionModel(Array.isArray(model) ? model : model != null ? [model] : [])
  }, [])

  const handleApply = async () => {
    const n =
      selectedPayload.emailChanges.length + selectedPayload.linkIdentityChanges.length
    if (n === 0) {
      toast.error(t('identitySyncSelectAtLeastOne', { ns: 'users' }))
      return
    }
    try {
      const result = await applySync(selectedPayload)
      toast.success(
        t('identitySyncApplySuccess', {
          ns: 'users',
          count: result.data.emailUpdatesApplied + result.data.linkIdentityApplied,
        }),
      )
      invalidateSystemUserCache.getSystemUsers()
      invalidateCustomer.getCustomerUsersForAdmins(undefined, undefined)
      invalidateIdentityUserSyncPreview()
      pop(false)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: string } })?.response?.data ??
        (e as Error)?.message
      toast.error(
        typeof msg === 'string' && msg.length > 0
          ? msg
          : t('unExpectedError', { ns: 'common-api-message' }),
      )
    }
  }

  const busy = isLoading || isFetching
  const selectedCount = selectionModel.length

  return (
    <Box display="flex" flexDirection="column" gap={2} pb={1} minWidth={{ xs: '100%', sm: 720 }}>
      {data != null && (
        <Typography variant="body2" color="text.secondary">
          {t('identitySyncCounts', {
            ns: 'users',
            isUsers: data.identityServerUserCount,
            localUsers: data.localUserCount,
          })}
        </Typography>
      )}

      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Button variant="outlined" size="small" onClick={() => refetch()} disabled={busy}>
          {t('identitySyncRefresh', { ns: 'users' })}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleApply}
          disabled={busy || isApplying || selectedCount === 0}
        >
          {isApplying ? <CircularProgress size={20} color="inherit" /> : t('identitySyncApply', { ns: 'users' })}
        </Button>
      </Box>

      {busy && !data ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography>{t('identitySyncNoChanges', { ns: 'users' })}</Typography>
      ) : (
        <GenericTable
          id="identity-user-sync-preview"
          data={gridRows}
          columns={columns}
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={handleSelectionChange}
          loading={busy}
          enablePagination={false}
          enableToolbar={false}
          autoHeight={false}
          height={420}
          maxHeight={420}
          getRowHeight={() => 'auto'}
          disableSelectionOnClick={false}
          disableMultipleSelection={false}
        />
      )}
    </Box>
  )
}

export default IdentityUserSyncDialog
